import type { AutocompleteChangeReason } from '@mui/material';
import type { AllProvidersToken } from '#utils/provider-filter.ts';

import * as React from 'react';

import { useFilters } from './FiltersContext.tsx';

const searchParamsKeyMap = {
  aId: 'addressIds',
  atId: 'appointmentTypeIds',
  astId: 'assessmentTypeIds',
  bcId: 'bookingChannelIds',
  pId: 'providerIds',
} as const;

export function useFilterAutocomplete<
  TDefault extends string | number,
  TData extends TDefault | { id: TDefault },
>(options: {
  searchParamKey: keyof typeof searchParamsKeyMap;
  defaultValue: TDefault[];
  /** Overrides the filter value written when an empty selection has a distinct domain representation. */
  emptyValue?: AllProvidersToken;
  data: Readonly<TData[]> | TData[] | undefined;
}) {
  // Cache the initial value to prevent further updates from re-running the
  // effect below.
  const [initialValue] = React.useState(() => options.defaultValue);

  // For some filters, the data is fetched asynchronously so we make the
  // assumption that if it's available on the first render, it has been
  // initialized. This way, we avoid synchronizing the data in the effect below.
  const [hasDataInitialized, setHasDataInitialized] = React.useState(
    () => (options.data ?? []).length > 0
  );

  const [value, setValue] = React.useState<TData[]>(() => getItemsById(options.data, initialValue));
  const valueRef = React.useRef(value);

  // Synchronize the data with the initial values once the data is available.
  React.useEffect(() => {
    if (!hasDataInitialized && options.data && initialValue.length > 0) {
      const initializedValue = getItemsById(options.data, initialValue);
      setValue(initializedValue);
      valueRef.current = initializedValue;
      setHasDataInitialized(true);
    }
  }, [hasDataInitialized, options.data, initialValue]);

  const filters = useFilters();

  function onChange(_event: unknown, values: TData[], reason: AutocompleteChangeReason) {
    setValue(values);
    valueRef.current = values;
    // The only way to select an option is by having the dropdown open, so we
    // wait for it to be closed before updating the search params.
    if (reason !== 'selectOption') {
      updateSearchParams(values);
    }
  }

  function onClose() {
    updateSearchParams(valueRef.current);
  }

  function updateSearchParams(value: TData[]) {
    filters.onChange({
      [searchParamsKeyMap[options.searchParamKey]]:
        value.length === 0 && options.emptyValue
          ? options.emptyValue
          : value.map((v) => (typeof v === 'object' ? String(v.id) : String(v))),
    });
  }

  function getAutocompleteProps() {
    return {
      multiple: true as const,
      disableCloseOnSelect: true,
      limitTags: 1,
      inputSize: 'medium' as const,
      value,
      onChange,
      onClose,
    };
  }

  return { getAutocompleteProps };
}

function getItemsById<TDefault extends string | number, TData extends TDefault | { id: TDefault }>(
  data: Readonly<TData[]> | TData[] | undefined,
  ids: TDefault[]
) {
  if (ids.length === 0) {
    return [];
  }

  const selectedIds = new Set(ids);
  return (data ?? []).filter((item) =>
    selectedIds.has(typeof item === 'object' ? item.id : (item as TDefault))
  );
}
